import { DecodedJwt } from '@sls-react/typings/services/iam/auth';
import { AppContext } from 'next/app';
import { KnownCookies } from '@sls-react/typings/cookies';
import { parseCookies } from 'nookies';
import redirect from '@sls-react/utils/redirect';
import { generateLoginLink } from '@sls-react/components/Links/LoginLink';
import decodeJwt from '@sls-react/utils/decodeJwt';
import universalSetCookie from '@sls-react/utils/universalSetCookie';
import { NextPageContext } from 'next';
import { dispatchLogoutEvent } from '@sls-react/lib/events/customEvents';
import { fetchNewAccessToken } from '@sls-react/api/iam/auth/sideline';

export interface NextContextSession {
  accessToken?: string;
  refreshToken?: string;
  clientId?: string;
  shouldRefetch: boolean;
  user?: DecodedJwt | null;

  loggedIn: boolean;
}

export function setAccessToken(
  ctx: NextPageContext | undefined,
  accessToken: string | undefined,
) {
  if (ctx?.session) {
    ctx.session.accessToken = accessToken;
  }
  universalSetCookie(ctx, 'access_token', accessToken);
}

export function setRefreshToken(
  ctx: NextPageContext | undefined,
  refreshToken: string | undefined,
) {
  if (ctx?.session) {
    ctx.session.refreshToken = refreshToken;
  }
  universalSetCookie(ctx, 'refresh_token', refreshToken);
}

export async function sessionContextLogout(
  ctx: NextPageContext | undefined,
  shouldRefetch: boolean,
) {
  setAccessToken(ctx, undefined);
  setRefreshToken(ctx, undefined);

  if (ctx?.session) {
    ctx.session.loggedIn = false;
  }

  if (typeof window !== 'undefined') {
    dispatchLogoutEvent();
  }

  // Could be undefined, ensure it's false.
  // If you need to redirect on the client, the SessionContext will take care of it
  if (shouldRefetch === false) {
    const params = {
      destination: ctx?.asPath,
      message: 'You were logged out',
    };
    throw await redirect(ctx!, generateLoginLink(params));
  }
}

export async function setupSessionContext({
  ctx,
  Component,
}: AppContext): Promise<NextContextSession> {
  const cookies: KnownCookies = ctx.req?.['cookies'] || parseCookies(ctx);

  // Refetch failed refresh requests if the page is not secure
  const shouldRefetch = !Component['securePage'];

  let accessToken = cookies.access_token;
  let refreshToken = cookies.refresh_token;

  let user = decodeJwt(accessToken);
  let loggedIn = !!user;

  if (accessToken) {
    if (user) {
      loggedIn = true;
    } else {
      // It's an invalid access token, destroy it
      accessToken = undefined;
    }
  }

  if (!user && refreshToken) {
    try {
      const tokenResponse = await fetchNewAccessToken.call(
        ctx,
        refreshToken,
        cookies.sls_client_id!,
      );

      user = decodeJwt(tokenResponse.access_token);

      if (user) {
        loggedIn = true;
      }

      accessToken = tokenResponse.access_token;
      refreshToken = tokenResponse.refresh_token;
      setAccessToken(ctx, accessToken);
      setRefreshToken(ctx, refreshToken);
    } catch (e) {
      accessToken = undefined;
      refreshToken = undefined;

      loggedIn = false;
      user = null;
    }
  }

  return {
    accessToken,
    loggedIn,
    refreshToken,
    shouldRefetch,
    user,
    clientId: cookies.sls_client_id,
  };
}
